// ДРУГ.4: публичный профиль другого игрока (тапом по чумбе или из поиска).
//
// Роут: /player/:id  (используется для своих чумб + игроков из поиска).
// Endpoint: GET /api/players/:id/public — без приватных данных
// (баланс/инвентарь/admin-заметка не возвращаются сервером).
//
// Кнопка «Пригласить на бой» — disabled до реализации дружеского PvP.

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ScreenLayout } from '../components/ScreenLayout';
import { HeroAvatar } from '../components/HeroAvatar';
import { getPublicProfile, type PublicProfileResponse } from '../api/client';
import { useToast } from '../components/Toast';
import styles from './ProfileTileScreens.module.css';

const FACTION_COLOR: Record<string, string> = {
  deus: 'var(--deus)', syntetics: 'var(--syn)', global: 'var(--gsec)', livlife: 'var(--liv)', neutral: 'var(--neu)'
};
const FACTION_LABEL: Record<string, string> = {
  deus: 'DEUS SYSTEMS', syntetics: 'SYNTETICS', global: 'GLOBAL SEC.', livlife: 'LIV LIFE', neutral: 'УЛИЦА'
};
const TIER_COLOR: Record<string, string> = {
  bronze: '#CD7F32', silver: '#B6BACA', gold: '#FFB13D'
};
const CATEGORY_LABEL: Record<string, string> = {
  combat: 'Боевые', collection: 'Коллекция', social: 'Социалка', season: 'Сезон', misc: 'Прочее'
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`;
}

function timeAgo(iso: string | null): string {
  if (!iso) return 'давно';
  const diffMs = Date.now() - new Date(iso).getTime();
  // Last_seen обновляется на сервере не чаще раз в минуту (throttle), плюс
  // лаг ответа — поэтому всё что < 2 мин считаем «в сети».
  const min = Math.floor(diffMs / 60000);
  if (min < 2) return 'в сети';
  if (min < 60) return `${min} мин назад`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} ч назад`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} д назад`;
  return formatDate(iso);
}

export function FriendProfileScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [data, setData] = useState<PublicProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const pid = parseInt(id || '', 10);
    if (!Number.isFinite(pid)) { setError('Некорректный ID'); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await getPublicProfile(pid);
        if (!cancelled) setData(r);
      } catch (e: any) {
        if (!cancelled) setError(e?.payload?.error === 'not_found' ? 'Игрок не найден' : 'Ошибка загрузки');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return <ScreenLayout title="ПРОФИЛЬ" backTo="/friends"><div className={styles.empty}>Загрузка…</div></ScreenLayout>;
  }
  if (error || !data) {
    return <ScreenLayout title="ПРОФИЛЬ" backTo="/friends"><div className={styles.empty}>{error || 'Нет данных'}</div></ScreenLayout>;
  }

  const p = data.player;
  const factionColor = FACTION_COLOR[p.faction] || 'var(--neu)';
  const accentColor = p.profileAccentColor ? `#${p.profileAccentColor}` : 'var(--t-hi)';

  // Группируем ачивки по категории.
  const byCat = new Map<string, typeof data.achievements>();
  for (const a of data.achievements) {
    if (!byCat.has(a.category)) byCat.set(a.category, []);
    byCat.get(a.category)!.push(a);
  }

  return (
    <ScreenLayout title="ПРОФИЛЬ" backTo="/friends">
      {/* Шапка */}
      <div style={{
        display: 'flex', gap: 14, alignItems: 'center',
        padding: '12px 0', borderBottom: '0.5px solid var(--line, rgba(160,170,200,0.18))'
      }}>
        <HeroAvatar id={p.heroId} size={64} faction={p.faction as any} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: accentColor, letterSpacing: 0.4 }}>
            {p.nick}
          </div>
          {p.activeTitle && (
            <div style={{ fontSize: 11, color: 'var(--neon-am, #FFB13D)', marginTop: 2 }}>
              «{p.activeTitle.text}»
            </div>
          )}
          <div style={{ fontSize: 11, color: factionColor, marginTop: 4, fontWeight: 600, letterSpacing: 0.3 }}>
            {FACTION_LABEL[p.faction] || p.faction.toUpperCase()}
          </div>
        </div>
      </div>

      {/* Лига / уровень / был в сети */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8,
        margin: '12px 0', padding: '10px 0',
        borderBottom: '0.5px solid var(--line, rgba(160,170,200,0.18))'
      }}>
        <div>
          <div style={{ fontSize: 10, color: 'var(--t-lo)', letterSpacing: 0.4 }}>ЛИГА</div>
          <div style={{ fontSize: 13, color: 'var(--t-hi)', fontWeight: 600, marginTop: 2 }}>{p.league}</div>
          <div style={{ fontSize: 10, color: 'var(--t-mute, #888)' }}>MMR {p.mmr}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--t-lo)', letterSpacing: 0.4 }}>УРОВЕНЬ</div>
          <div style={{ fontSize: 13, color: 'var(--t-hi)', fontWeight: 600, marginTop: 2 }}>{p.accountLevel}</div>
          <div style={{ fontSize: 10, color: 'var(--t-mute, #888)' }}>с {formatDate(p.regDate)}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: 'var(--t-lo)', letterSpacing: 0.4 }}>В СЕТИ</div>
          <div style={{ fontSize: 13, color: 'var(--t-hi)', fontWeight: 600, marginTop: 2 }}>{timeAgo(p.lastSeenAt)}</div>
        </div>
      </div>

      {/* Статистика боёв */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 10, color: 'var(--t-md)', letterSpacing: 1, marginBottom: 8 }}>СТАТИСТИКА БОЁВ</div>
        {data.stats.total === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--t-lo)', padding: 8 }}>Ещё не играл</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 10, color: 'var(--t-lo)' }}>МАТЧЕЙ</div>
                <div style={{ fontSize: 18, color: 'var(--t-hi)', fontWeight: 700 }}>{data.stats.total}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--t-lo)' }}>ПОБЕД</div>
                <div style={{ fontSize: 18, color: 'var(--neon-li, #b4ff00)', fontWeight: 700 }}>{data.stats.wins}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--t-lo)' }}>WINRATE</div>
                <div style={{ fontSize: 18, color: 'var(--neon-cy, #3DF0FF)', fontWeight: 700 }}>{data.stats.winrate}%</div>
              </div>
            </div>
            {data.stats.byFaction.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {data.stats.byFaction.map(f => (
                  <div key={f.faction} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    fontSize: 11, padding: '4px 8px',
                    borderLeft: `2px solid ${FACTION_COLOR[f.faction] || 'var(--neu)'}`,
                    background: 'rgba(255,255,255,0.02)'
                  }}>
                    <span style={{ color: FACTION_COLOR[f.faction] || 'var(--neu)', fontWeight: 600 }}>
                      {(FACTION_LABEL[f.faction] || f.faction).split(' ')[0]}
                    </span>
                    <span style={{ color: 'var(--t-mid)' }}>
                      {f.count} матч. · {f.winrate}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Достижения */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 10, color: 'var(--t-md)', letterSpacing: 1, marginBottom: 8 }}>
          ДОСТИЖЕНИЯ ({data.achievements.length})
        </div>
        {data.achievements.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--t-lo)', padding: 8 }}>Пока ничего не разблокировал</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Array.from(byCat.entries()).map(([cat, items]) => (
              <div key={cat}>
                <div style={{ fontSize: 10, color: 'var(--t-lo)', marginBottom: 4, letterSpacing: 0.3 }}>
                  {CATEGORY_LABEL[cat] || cat}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {items.map(a => (
                    <div key={a.code} title={a.description}
                      style={{
                        padding: '5px 10px',
                        background: 'rgba(255,255,255,0.04)',
                        borderLeft: `2px solid ${a.tier ? TIER_COLOR[a.tier] : 'var(--t-lo)'}`,
                        fontSize: 11, color: 'var(--t-hi)'
                      }}>
                      {a.title}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Действия — приглашение на бой (заглушка). */}
      {!data.isSelf && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button
            disabled
            title="Дружеский PvP в разработке"
            className="cd"
            style={{
              flex: 1, padding: '12px',
              background: 'transparent',
              color: 'var(--t-mute, #888)',
              border: '0.5px dashed var(--line, #555)',
              fontSize: 12, fontWeight: 600,
              letterSpacing: 0.4, textTransform: 'uppercase',
              cursor: 'not-allowed'
            }}
            onClick={() => toast.show('Дружеский PvP в разработке', 'amber')}
          >
            ⚔ Пригласить на бой (скоро)
          </button>
        </div>
      )}
      {data.isFriend && (
        <div style={{ marginTop: 8, fontSize: 10, color: 'var(--neon-li, #b4ff00)', textAlign: 'center', letterSpacing: 0.4 }}>
          ✓ В чумбах
        </div>
      )}
      {/* navigate suppress unused */}
      {false && <span onClick={() => navigate('/friends')} />}
    </ScreenLayout>
  );
}
